import { FunctionComponent } from 'react';
import Image from 'next/image';
import { ImgproxyOptions, insertImgproxyOptions, isImgproxyUrl } from '@utils/imgproxy';

// imgix's own fit vocabulary — kept because it's what the codebase already
// uses everywhere, not because ChopImage depends on imgix internally.
export type ChopImageFit = 'clip' | 'crop' | 'fill' | 'max' | 'scale';

const IMGPROXY_RESIZING_TYPE: Record<ChopImageFit, NonNullable<ImgproxyOptions['resizingType']>> = {
  clip: 'fit',
  crop: 'fill',
  fill: 'fill',
  max: 'fit',
  scale: 'force',
};

export interface ChopImageProps {
  alt?: string;
  className?: string;
  'data-testid'?: string;
  dpr?: number;
  fit?: ChopImageFit;
  height?: number;
  quality?: number;
  src: string;
  width?: number;
}

/**
 * Renders images from imgproxy or untouched third-party/external sources:
 *   - Our own imgproxy URLs (already built server-side) get their
 *     processing options spliced in and render through next/image, via a
 *     custom loader — that bypasses next/image's own remotePatterns
 *     check, so no next.config.js changes are needed for the imgproxy
 *     domain.
 *   - Anything else (third-party avatars, unrecognized sources) renders
 *     as a plain <img>, untouched — imgproxy isn't configured to fetch
 *     arbitrary external hosts, so these must never be proxied, and
 *     next/image's built-in loader would otherwise throw on an
 *     unconfigured remote host.
 *
 * See chop/web-client's ChopImage for the original reference and design
 * notes.
 */
export const ChopImage: FunctionComponent<ChopImageProps> = ({
  alt,
  className,
  'data-testid': dataTestId,
  dpr,
  fit,
  height,
  quality,
  src,
  width,
}) => {
  const resolvedAlt = alt ?? '';

  if (!isImgproxyUrl(src)) {
    // Not ours — e.g. a third-party OAuth avatar (Facebook, Google).
    // imgproxy isn't allowlisted to fetch arbitrary external hosts, so
    // this renders untouched instead of being proxied. fit/quality/dpr
    // have no effect here — there's no processing pipeline to apply them.
    return (
      <img
        alt={resolvedAlt}
        className={className}
        data-testid={dataTestId}
        height={height}
        src={src}
        width={width}
      />
    );
  }

  const resizingType = fit ? IMGPROXY_RESIZING_TYPE[fit] : undefined;

  if (height === undefined || width === undefined) {
    // next/image's layout='fixed' requires both dimensions and renders a
    // fixed-size box — forcing the missing one to match would distort
    // non-square artwork (e.g. image moment thumbnails, next-step icons).
    // Fall back to a plain <img>, like the imgix and untouched branches
    // above: the browser scales the missing dimension to the image's
    // natural aspect ratio, and imgproxy (given 0 for that side via
    // insertImgproxyOptions) resizes the source to match.
    return (
      <img
        alt={resolvedAlt}
        className={className}
        data-testid={dataTestId}
        height={height}
        src={insertImgproxyOptions(src, { dpr, height, quality, resizingType, width })}
        width={width}
      />
    );
  }

  return (
    <Image
      alt={resolvedAlt}
      className={className}
      data-testid={dataTestId}
      height={height}
      layout='fixed'
      loader={({ quality: srcSetQuality, width: srcSetWidth }) =>
        insertImgproxyOptions(src, {
          dpr,
          height,
          quality: srcSetQuality ?? quality,
          resizingType,
          width: srcSetWidth || width,
        })
      }
      src={src}
      width={width}
    />
  );
};
